import { useState } from "react";
import { Building2, CheckCircle2, Fingerprint, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { KehadiranBadge } from "../../components/common";
import { CameraCapture } from "../../components/CameraCapture";
import { usePortalAuth } from "../../context/PortalAuthContext";
import { portalApi } from "../../lib/portalApi";
import { errMsg } from "../../lib/api";
import { fmtDate } from "../../lib/format";
import { detectFaces, loadFace } from "../../lib/face";
import FaceRegister from "./FaceRegister";

const TodayCard = ({ rec }) => (
  <div data-testid="portal-today-done" className="fade-up rounded-2xl border border-emerald-200 bg-white p-4 text-center">
    <CheckCircle2 className="mx-auto h-10 w-12 text-emerald-600" />
    <p className="mt-3 font-heading text-xl font-semibold text-gray-900">Sudah check-in</p>
    <p className="mt-1 font-mono text-4xl font-semibold text-primary" data-testid="portal-today-jam">{rec.jam}</p>
    <p className="mt-1 text-sm text-gray-500">{fmtDate(rec.tanggal)}</p>
    <div className="mt-4 flex justify-center"><KehadiranBadge status={rec.status} testid="portal-today-status" /></div>
    {rec.status === "perlu_dicek" && <p className="mt-3 text-xs text-gray-500">Wajah belum cocok otomatis. Admin akan memeriksa kehadiran Anda.</p>}
  </div>
);

export default function PortalHome() {
  const { me, refresh } = usePortalAuth();
  const [step, setStep] = useState("idle");
  const [msg, setMsg] = useState("");

  if (!me.face_registered) return <FaceRegister />;
  if (me.hari_ini) return <TodayCard rec={me.hari_ini} />;

  const start = () => {
    setMsg("");
    loadFace().catch(() => {});
    setStep("kamera");
  };

  const onCapture = async (canvas) => {
    setStep("proses");
    let descriptor = null;
    try {
      const faces = await detectFaces(canvas);
      descriptor = faces.length === 1 ? faces[0] : null;
    } catch { descriptor = null; }
    try {
      await portalApi.post("/portal/checkin", { photo: canvas.toDataURL("image/jpeg", 0.85), descriptor });
      toast.success("Check-in berhasil");
      await refresh();
    } catch (e) {
      setStep("idle");
      setMsg(errMsg(e));
    }
  };

  const p = me.perusahaan;
  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-gray-200 bg-white p-5">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Hari ini · {fmtDate(me.tanggal)}</p>
        <p data-testid="portal-today-pending" className="mt-1 font-heading text-lg font-semibold text-amber-700">Belum check-in</p>
        <p className="mt-2 flex items-center gap-1.5 text-sm text-gray-600">
          <Building2 className="h-4 w-4 shrink-0 text-primary" />
          {p ? p.nama : "Belum terdaftar di perusahaan"}
        </p>
      </div>

      {msg && (
        <div data-testid="portal-checkin-error" className="flex items-start gap-2 rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{msg}
        </div>
      )}

      {step === "idle" ? (
        <button data-testid="portal-checkin-btn" onClick={start}
          className="mx-auto flex aspect-square w-56 flex-col items-center justify-center rounded-full bg-primary text-white shadow-xl shadow-emerald-500/25 transition-transform active:scale-95 disabled:opacity-80">
          <Fingerprint className="h-14 w-14" />
          <span className="mt-3 font-heading text-xl font-semibold">Check-in</span>
          <span className="mt-1 text-xs text-emerald-100">Scan wajah</span>
        </button>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-700">Hadapkan wajah ke kamera</p>
            <Button variant="ghost" data-testid="portal-checkin-cancel" className="h-10" onClick={() => setStep("idle")} disabled={step === "proses"}><X className="mr-1 h-4 w-4" />Batal</Button>
          </div>
          <CameraCapture onCapture={onCapture} busy={step === "proses"} busyLabel="Menyimpan check-in…" label="Ambil Selfie & Check-in" testid="checkin-camera" />
        </div>
      )}
    </div>
  );
}
